import React, { useEffect, useMemo, useState } from 'react';
import EmployeePicker from '../../components/EmployeePicker';
import Pagination from '../../components/Pagination';
import BulkActionBar from '../../components/BulkActionBar';
import SegmentedFilterBar from '../../components/SegmentedFilterBar';
import ScopeToggle from '../../components/ScopeToggle';
import { useScope } from '../../lib/hooks/useScope';
import { usePaginated } from '../../lib/hooks/usePaginated';
import { useRowSelection } from '../../lib/hooks/useRowSelection';
import { useSortable, SortIcon } from '../../lib/hooks/useSortable';
import { formatDate } from '../../lib/format';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { Plus, Trash2, Edit2, X, Briefcase, Users } from 'lucide-react';
import { toast } from 'sonner';
import LocationCombobox from '../../components/LocationCombobox';

const TYPES = ['PMC', 'Cost Consultant', 'Structural Consultant', 'Architect'];
const CRM_TAGS = ['Decision Maker', 'Influencer', 'Recommander', 'Consultant', 'Architect', 'Owner/MD', 'Govt Official', 'VIP'];

const initialForm = {
  name: '', industry: 'Consulting', website: '', size: '', address: '', location: '', country: 'India',
  annual_revenue: '', owner: '', tags: [], notes: '',
  is_consultant: true, consultant_type: 'PMC',
};

/**
 * Consultants / Architects page.
 *
 * Reuses the Companies collection with `is_consultant=true` + `consultant_type` field.
 * Mirrors the Companies page UX but scoped to consultant firms.
 */
export default function Consultants() {
  const [firms, setFirms] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [filtered, setFiltered] = useState([]);
  const { scope, setScope } = useScope('consultants');
  const { sorted, sortKey, sortDir, getProps } = useSortable(filtered, { defaultKey: 'created_at', defaultDir: 'desc' });

  const pagination = usePaginated(sorted, { defaultPageSize: 10 });
  const pageIds = pagination.paged.map((c) => c.id);
  const selection = useRowSelection();

  const filterDefs = useMemo(() => {
    const locations = Array.from(new Set(firms.map((c) => c.location || [c.city, c.state].filter(Boolean).join(', ')).filter(Boolean))).sort();
    const owners = Array.from(new Set(firms.map((c) => c.owner).filter(Boolean))).sort();
    return [
      { key: 'consultant_type', label: 'Type', type: 'multiSelect', options: TYPES },
      { key: 'location', label: 'Location', type: 'multiSelect', options: locations },
      { key: 'tags', label: 'Tags', type: 'tags', options: (allTags.length ? allTags.map((t) => t.name || t) : CRM_TAGS) },
      { key: 'owner', label: 'Owner', type: 'multiSelect', options: owners },
    ];
  }, [firms, allTags]);

  const load = async () => {
    if (!scope) return;
    try {
      const [r, tagRes] = await Promise.all([
        apiGet(`/crm/companies?is_consultant=true&scope=${scope}`),
        apiGet('/crm/tags').catch(() => ({ tags: [] })),
      ]);
      setFirms(r.companies || []);
      setAllTags(tagRes.tags || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const openAdd = () => { setEditing(null); setForm(initialForm); setShowForm(true); };
  const openEdit = (c, e) => {
    e?.stopPropagation();
    const legacyLoc = c.location || [c.city, c.state].filter(Boolean).join(', ');
    setEditing(c);
    setForm({ ...initialForm, ...c, location: legacyLoc, is_consultant: true });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        is_consultant: true,
        // Coerce empty strings to null for numeric fields
        annual_revenue: form.annual_revenue === '' ? null : Number(form.annual_revenue),
      };
      if (editing) {
        const updated = await apiPut(`/crm/companies/${editing.id}`, payload);
        toast.success('Consultant firm updated');
        // Optimistic instant update
        setFirms(prev => prev.map(f => f.id === editing.id ? { ...f, ...updated } : f));
      } else {
        await apiPost('/crm/companies', payload);
        toast.success('Consultant firm added');
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.detail || 'Save failed');
    }
  };

  const remove = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Remove this consultant firm? Linked projects will keep the consultant tag.')) return;
    await apiDel(`/crm/companies/${id}`);
    toast.success('Removed');
    load();
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selection.selected);
    if (!ids.length || !window.confirm(`Remove ${ids.length} consultant firm(s)?\n\nThis action cannot be undone.`)) return;
    try {
      await Promise.all(ids.map((id) => apiDel(`/crm/companies/${id}`)));
      toast.success('Removed');
      selection.clear();
      load();
    } catch { toast.error('Bulk delete failed'); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Business Development</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Consultants / Architects</h1>
          <p className="text-sm text-slate-600 mt-1">External advisory firms — PMC, Cost, Structural, Architect — that you collaborate with on projects.</p>
        </div>
        <button onClick={openAdd} className="btn-primary flex items-center gap-2" data-testid="add-consultant-btn">
          <Plus size={16} /> Add Consultant
        </button>
      </div>

      <div className="mb-3 flex items-center justify-between">
        <ScopeToggle value={scope} onChange={setScope} testid="scope-toggle-consultants" />
        <div className="text-xs text-slate-500">
          Showing <b className="text-[#3b4872]">{firms.length}</b> consultant firms · <span className="uppercase tracking-wider font-display font-bold text-[10px]">{scope}</span>
        </div>
      </div>

      <SegmentedFilterBar entityType="consultants" items={firms.map((c) => ({ ...c, location: c.location || [c.city, c.state].filter(Boolean).join(', ') }))} filterDefs={filterDefs} onFiltered={setFiltered} />
      <div className="mb-3"></div>

      <BulkActionBar count={selection.count} onClear={selection.clear} className="mb-3">
        <button onClick={handleBulkDelete} className="text-xs font-display font-bold uppercase tracking-wider px-3 py-1.5 bg-red-600 text-white rounded-sm hover:bg-red-700 flex items-center gap-1" data-testid="bulk-delete-consultants"><Trash2 size={11} /> Delete</button>
      </BulkActionBar>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full dense-table">
          <thead><tr>
            <th className="w-10"><input type="checkbox" className="w-4 h-4 accent-[#3b4872] cursor-pointer" checked={selection.allSelected(pageIds)} ref={(el) => { if (el) el.indeterminate = selection.someSelected(pageIds); }} onChange={() => selection.toggleAll(pageIds)} data-testid="select-all-consultants" /></th>
            <th {...getProps('name')}>Firm <SortIcon dir={sortKey === 'name' ? sortDir : null} /></th>
            <th {...getProps('consultant_type')}>Type <SortIcon dir={sortKey === 'consultant_type' ? sortDir : null} /></th>
            <th {...getProps('location')}>Location <SortIcon dir={sortKey === 'location' ? sortDir : null} /></th>
            <th {...getProps('contact_count')}>Contacts <SortIcon dir={sortKey === 'contact_count' ? sortDir : null} /></th>
            <th {...getProps('owner')}>Account Manager <SortIcon dir={sortKey === 'owner' ? sortDir : null} /></th>
            <th {...getProps('created_at')}>Created <SortIcon dir={sortKey === 'created_at' ? sortDir : null} /></th>
            <th></th>
          </tr></thead>
          <tbody>
            {pagination.paged.map((c) => (
              <tr key={c.id} className={`hover:bg-slate-50 ${selection.isSelected(c.id) ? 'bg-indigo-50/40' : ''}`} data-testid={`consultant-row-${c.id}`}>
                <td><input type="checkbox" className="w-4 h-4 accent-[#3b4872] cursor-pointer" checked={selection.isSelected(c.id)} onChange={() => selection.toggle(c.id)} /></td>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-sm bg-[#3b4872]/10 text-[#3b4872] flex items-center justify-center font-display font-bold text-xs">
                      <Briefcase size={14} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-[#0F172A] truncate">{c.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">{c.size || '—'}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-sm font-display font-bold">{c.consultant_type || '—'}</span>
                </td>
                <td className="text-xs">{c.location || (c.city ? `${c.city}${c.state ? ', ' + c.state : ''}` : '—')}</td>
                <td className="text-xs"><span className="inline-flex items-center gap-1 text-slate-600"><Users size={11} /> {c.contact_count ?? '—'}</span></td>
                <td className="text-xs">{c.owner || '—'}</td>
                <td className="text-xs whitespace-nowrap">{formatDate(c.created_at)}</td>
                <td>
                  <div className="flex gap-2">
                    <button onClick={(e) => openEdit(c, e)} className="text-slate-500 hover:text-[#3b4872]"><Edit2 size={14} /></button>
                    <button onClick={(e) => remove(c.id, e)} className="text-slate-500 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!firms.length && <tr><td colSpan="8" className="p-8 text-center text-sm text-slate-500">No consultants yet. Add your first PMC, Cost, Structural or Architect firm to get started.</td></tr>}
          </tbody>
        </table>
        {firms.length > 0 && (
          <div className="border-t border-slate-200 px-4 py-3">
            <Pagination page={pagination.page} pageSize={pagination.pageSize} total={pagination.total} start={pagination.start} end={pagination.end} onPageChange={pagination.setPage} onPageSizeChange={pagination.setPageSize} label="firms" />
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">{editing ? 'Edit Consultant Firm' : 'Add Consultant / Architect'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-slate-500"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="label">Firm Name *</label>
                  <input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="consultant-form-name" />
                </div>
                <div>
                  <label className="label">Consultant Type *</label>
                  <select className="input" required value={form.consultant_type} onChange={(e) => setForm({ ...form, consultant_type: e.target.value })} data-testid="consultant-form-type">
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div><label className="label">Size</label><select className="input" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })}><option value="">—</option>{['1-10', '11-50', '51-200', '201-500', '500+'].map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
                <div><label className="label">Website</label><input className="input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" /></div>
                <div className="col-span-2">
                  <label className="label">Location</label>
                  <LocationCombobox value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="consultant-location" />
                </div>
                <div className="col-span-2"><label className="label">Address</label><textarea className="input" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div className="col-span-2"><label className="label">Account Manager</label><EmployeePicker value={form.owner} onChange={(v) => setForm({ ...form, owner: v })} testid="consultant-owner-picker" restrictToTeam /></div>
                <div className="col-span-2">
                  <label className="label">Tags</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {CRM_TAGS.map(t => {
                      const sel = (form.tags || []).includes(t);
                      return (
                        <button key={t} type="button" onClick={() => setForm(f => ({ ...f, tags: sel ? (f.tags || []).filter(x => x !== t) : [...(f.tags || []), t] }))} className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${sel ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'border-slate-300 text-slate-600 hover:border-[#3b4872]'}`}>{t}</button>
                      );
                    })}
                  </div>
                </div>
                <div className="col-span-2"><label className="label">Notes</label><textarea className="input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="consultant-form-submit">{editing ? 'Update' : 'Create'} Firm</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
